import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { AccountService } from '../services/account.service';
import { AuthenticationService } from '../services/authentication.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { AccountViewModel } from '../shared/models/update-account-view-model';
import { ToastService } from '../shared/toast/toast.service';

@Component({
  selector: 'pps-manage-user',
  templateUrl: './manage-user.component.html',
  styles: [
  ],
})
export class ManageUserComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  items: MenuItem[] = [
    {label: "Admin", icon: "pi-fw fas fa-user-cog"},
    {label: "Manage Users", icon: "pi-fw fas fa-users"}
  ];
  
  spinnerName: string = "AccountSpinner";
  accounts: AccountViewModel[] = [];
  gridHeaders: any[];
  context:any = {};
  entityLoading: boolean = false;
  selectedRow: AccountViewModel = null;

  gridOptions: any = {
    getRowId: param => param.data.userId,
    onRowClicked: this.onRowClicked,
    defaultColDef: {
      resizable: true,
      floatingFilter: false,
      filter: true,
      sortable: true
    }
  };

  private gridApi;
  smallScreen: boolean = false;

  constructor(private _accountService: AccountService,
              private _spinner: NgxSpinnerService,
              private _util: PpsUtilityService,
              private _router: Router,
              private _confirmationService: ConfirmationService,
              private _toast: ToastService,
              private breakpointObserver: BreakpointObserver) {
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });    
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadData();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }  

  onRowClicked(evt){
    let _this = evt.context.componentParent;
    var selectedRows = _this.gridApi.getSelectedRows();
    _this.selectedRow = selectedRows.length === 1 ? selectedRows[0] : null;
  }

  addAccount(){
    this._router.navigate(["/add-user"], { replaceUrl: true});
  }

  resetPassword(account: AccountViewModel){
    this._router.navigate(["/reset-password"], { replaceUrl: true, queryParams: account});
  }

  loadData(){
    this._spinner.show("AccountSpinner");
    this.entityLoading = true;
    this.accounts.length = 0;
    this.selectedRow = null;
    this.subs.sink = this._accountService.getAll()
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.accounts = apiResponse.result;
          this._toast.succeeded(`${this.accounts.length} accounts loaded`);
        }else{
          this._toast.failed(`Failed to load accounts: ${apiResponse.errors.join(";")}`);
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide("AccountSpinner");
        this.entityLoading = false;
      });
  }

  deleteAccount(account: AccountViewModel){
    console.log(account);
    if (account === null){
      return;
    }

    this._confirmationService.confirm({
      header: "Delete Account",
      message: `Are you sure that you want to delete the account: ${account.userName}?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => {
        this.subs.sink = this._accountService.delete(account.userId)
        .subscribe(apiResponse => {
          if (apiResponse.succeed){
            var foundIndex = this.accounts.findIndex(x => x.userId == account.userId);
            if (foundIndex !== -1 ){
              this.accounts.splice(foundIndex, 1);
              this.gridApi.setRowData(this.accounts);
              this.selectedRow = null;
              this._toast.succeeded(`Account [${account.userName}] deleted!`);
            }else{
              this._toast.failed(`Delete account [${account.userName}] failed!`);
            }
          }else{
            this._toast.failed(`Failed to Delete account [${account.userName}]: ${apiResponse.errors.join("|")}`);
          }
        });
      }
    });
  }

  private getColumnDefs(){
    let cols = [
      {
        headerName: 'Username',
        field:'userName',
        width: 120
      },
      {
        headerName: 'First Name',
        field:'firstName',
        width: 200
      },
      {
        headerName: 'Last Name',
        field:'lastName',
        width: 200
      },
      {
        headerName: 'Email',
        field:'email',
        flex: 1
      },
      {
        headerName: 'Date Created',
        field:'addDate',
        width: 200,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        valueGetter: this.getDateValue
      }
    ];

    return cols;
  }

  private getDateValue(params){
    if (params.data[params.colDef.field]){
      let value = params.context.componentParent._util.getDateString(params.data[params.colDef.field]);
      return value;
    }

    return "";
  }
}
